/**
 * Trade-show lead capture client + offline outbox (TCGMA-175).
 *
 * The booth reality: venue wifi is frequently saturated or captive-portalled, and
 * the app is served from the Pi over a tunnel — so the network WILL drop at some
 * point during a show. Losing a visitor's email is the one unacceptable failure
 * (they're standing right there; you don't get a second try). So a submit that
 * can't reach the server is queued in localStorage and replayed when it can.
 *
 * We deliberately do NOT build a full PWA/service worker for this: the CSP is
 * hash-pinned and browsing offline is only a nice-to-have. Protecting the leads
 * is the 90/10.
 *
 * Replay is safe because the server dedupes on (email, event) — a queued lead
 * that actually made it through the first time updates that row instead of
 * creating a duplicate.
 */

const OUTBOX_KEY = "tcg-web.lead_outbox_v1";

export interface LeadDraft {
  email: string;
  name?: string | null;
  phone?: string | null;
  interests?: string[];
  marketingConsent?: boolean;
  raffleEntry?: boolean;
  eventName?: string | null;
  source?: "kiosk" | "qr" | "web";
  /** Honeypot passthrough. Hidden from humans; the server discards any lead
   *  that has it set. Must be named `company` to match the server check. */
  company?: string;
}

type QueuedLead = LeadDraft & { queuedAt: number };

/** Thrown for a 4xx — the submission itself is wrong (e.g. a typo'd email), so
 *  it must be shown to the person rather than silently queued forever. */
export class LeadValidationError extends Error {}

function readQueue(): QueuedLead[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // corrupt/unavailable storage — never let this break the form
  }
}

function writeQueue(queue: QueuedLead[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue));
  } catch {
    /* quota/private-mode — nothing sensible to do, the submit already failed */
  }
}

export function queuedLeadCount(): number {
  return readQueue().length;
}

async function postLead(lead: LeadDraft): Promise<Response> {
  return fetch("/api/store/leads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lead),
  });
}

export type SubmitResult = { status: "sent" } | { status: "queued" };

/**
 * Submit a lead. Returns "queued" when the network is down — the caller should
 * still show success, because from the visitor's side it IS done: we have their
 * email and it will sync.
 */
export async function submitLead(lead: LeadDraft): Promise<SubmitResult> {
  try {
    const res = await postLead(lead);
    if (res.ok) return { status: "sent" };
    if (res.status >= 400 && res.status < 500) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new LeadValidationError(body.error ?? "Please check the details.");
    }
    // 5xx — server-side hiccup, not the visitor's problem. Keep the lead.
    writeQueue([...readQueue(), { ...lead, queuedAt: Date.now() }]);
    return { status: "queued" };
  } catch (err) {
    if (err instanceof LeadValidationError) throw err;
    // Network failure (offline / DNS / tunnel down) — this is what the outbox exists for.
    writeQueue([...readQueue(), { ...lead, queuedAt: Date.now() }]);
    return { status: "queued" };
  }
}

/**
 * Replay everything queued. Returns how many were cleared. Safe to call often —
 * a lead that already landed just dedupes server-side.
 */
export async function flushLeadQueue(): Promise<number> {
  const queue = readQueue();
  if (queue.length === 0) return 0;

  const stillQueued: QueuedLead[] = [];
  let cleared = 0;

  for (const lead of queue) {
    try {
      const res = await postLead(lead);
      if (res.ok || (res.status >= 400 && res.status < 500)) {
        // 4xx here means it can never succeed (bad email captured at the booth).
        // Drop it rather than retrying forever — it's already lost, and a stuck
        // outbox would block the good ones behind it.
        cleared++;
      } else {
        stillQueued.push(lead);
      }
    } catch {
      stillQueued.push(lead); // still offline — try again next time
    }
  }

  writeQueue(stillQueued);
  return cleared;
}

/**
 * Flush now, and again whenever the browser regains connectivity. Returns a
 * cleanup fn. Call once from the signup page.
 */
export function startLeadSync(onFlush?: (cleared: number) => void): () => void {
  const run = () => {
    void flushLeadQueue().then((n) => {
      if (n > 0) onFlush?.(n);
    });
  };
  run();
  window.addEventListener("online", run);
  return () => window.removeEventListener("online", run);
}
